import { useEffect, useMemo, useState } from 'react';
import {
  createGooglePlacesLocationLoadOptions,
  ensureGoogleMapsPlacesLoaded,
  type GooglePlacesLocationOption,
} from '@anyroad/google-maps';

export type UseGooglePlacesLocationArgs = {
  apiKey?: string | null;
  country?: string[] | string;
  types?: string[];
};

export type GooglePlacesLocationLoadOptions = (
  inputValue: string
) => Promise<GooglePlacesLocationOption[]>;

export const useGooglePlacesLocation = ({
  apiKey,
  country,
  types,
}: UseGooglePlacesLocationArgs): {
  error: Error | null;
  isReady: boolean;
  loadOptions: GooglePlacesLocationLoadOptions;
} => {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!apiKey) {
      setIsReady(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    setIsReady(false);
    setError(null);

    ensureGoogleMapsPlacesLoaded(apiKey)
      .then(() => {
        if (cancelled) return;
        setIsReady(true);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setIsReady(false);
        setError(e instanceof Error ? e : new Error(String(e)));
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  const countryKey = JSON.stringify(country ?? null);
  const typesKey = JSON.stringify(types ?? null);

  const loadOptions = useMemo(() => {
    const stableCountry = JSON.parse(countryKey) as string[] | string | null;
    const stableTypes = JSON.parse(typesKey) as string[] | null;

    return createGooglePlacesLocationLoadOptions({
      apiKey: apiKey ?? '',
      country: stableCountry ?? undefined,
      types: stableTypes ?? undefined,
    });
  }, [apiKey, countryKey, typesKey]);

  return { error, isReady, loadOptions };
};

